import { useState, useCallback } from 'react';
import {
  Flavor,
  FlavorLabels,
  FLAVOR_LABELS,
  FLAVOR_STORAGE_KEY,
  DEFAULT_FLAVOR,
} from '../config/themeConfig';

/**
 * Hook that returns the active flavor labels and a setter to change the flavor.
 *
 * The selected flavor is persisted in localStorage so it survives page reloads.
 * On first visit the default flavor ("military") is used.
 *
 * @example
 * const { flavor, labels, setFlavor } = useFlavorConfig();
 * <Typography>{labels.activeCoursesLabel}</Typography>
 */
export function useFlavorConfig(): {
  flavor: Flavor;
  labels: FlavorLabels;
  setFlavor: (f: Flavor) => void;
} {
  const readFromStorage = (): Flavor => {
    try {
      const stored = localStorage.getItem(FLAVOR_STORAGE_KEY);
      if (stored && stored in FLAVOR_LABELS) {
        return stored as Flavor;
      }
    } catch {
      // localStorage not available (e.g. SSR or private mode)
    }
    return DEFAULT_FLAVOR;
  };

  const [flavor, setFlavorState] = useState<Flavor>(readFromStorage);

  const setFlavor = useCallback((newFlavor: Flavor) => {
    setFlavorState(newFlavor);
    try {
      localStorage.setItem(FLAVOR_STORAGE_KEY, newFlavor);
    } catch {
      // localStorage not available
    }
  }, []);

  return { flavor, labels: FLAVOR_LABELS[flavor], setFlavor };
}
